import { useQuery } from '@tanstack/react-query';
import { useQuery as useConvexQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Users, FileText, Building2, Flag } from 'lucide-react';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDashboardStats } from '../utils/api';
import { formatNumber, formatTimeAgo } from '../utils/helpers';
import { useAdmin } from '../contexts/AdminContext';

export default function Dashboard() {
    const { admin } = useAdmin();

    // Fetch stats from REST API
    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: getDashboardStats,
    });

    // Fetch recent activity logs from admin Convex
    const recentLogs = useConvexQuery(api.activityLogs.getRecentLogs, { limit: 10 });

    if (statsLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    const statCards = [
        {
            title: 'Total Users',
            value: formatNumber(stats?.data?.users?.total || 0),
            icon: Users,
            color: 'blue',
            subtitle: `${formatNumber(stats?.data?.users?.verified || 0)} verified`,
        },
        {
            title: 'Total Posts',
            value: formatNumber(stats?.data?.posts?.total || 0),
            icon: FileText,
            color: 'green',
            subtitle: `${formatNumber(stats?.data?.posts?.today || 0)} today`,
        },
        {
            title: 'Communities',
            value: formatNumber(stats?.data?.users?.communities || 0),
            icon: Building2,
            color: 'purple',
            subtitle: 'Active communities',
        },
        {
            title: 'Pending Reports',
            value: formatNumber(stats?.data?.reports?.pending || 0),
            icon: Flag,
            color: 'red',
            subtitle: 'Need attention',
        },
    ];

    return (
        <div className="space-y-6">
            {/* Welcome Message */}
            <div>
                <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Welcome back, {admin?.email || 'Admin'}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat) => (
                    <Card key={stat.title}>
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    {stat.title}
                                </p>
                                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                                <p className="text-xs text-muted-light dark:text-muted-dark">
                                    {stat.subtitle}
                                </p>
                            </div>
                            <div className={`p-3 rounded-lg bg-${stat.color}/10`}>
                                <stat.icon size={24} className={`text-${stat.color}`} />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Recent Activity */}
            <Card title="Recent Activity">
                {!recentLogs ? (
                    <LoadingSpinner />
                ) : recentLogs.length === 0 ? (
                    <p className="text-muted-light dark:text-muted-dark text-center py-8">
                        No recent activity
                    </p>
                ) : (
                    <div className="space-y-3">
                        {recentLogs.map((log) => (
                            <div
                                key={log._id}
                                className="flex items-start gap-3 p-3 rounded-lg bg-accent-light dark:bg-accent-dark"
                            >
                                <div className="flex-1">
                                    <p className="font-medium">{formatActionLabel(log.action)}</p>
                                    {log.details?.metadata && (
                                        <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                            {JSON.stringify(log.details.metadata).substring(0, 100)}
                                        </p>
                                    )}
                                </div>
                                <span className="text-xs text-muted-light dark:text-muted-dark whitespace-nowrap">
                                    {formatTimeAgo(log.timestamp)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Quick Actions */}
            <Card title="Quick Actions">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <a
                        href="/verifications"
                        className="p-4 rounded-lg border border-border-light dark:border-border-dark hover:bg-accent-light dark:hover:bg-accent-dark transition-colors"
                    >
                        <h4 className="font-semibold mb-1">Verification Requests</h4>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            Review pending applications
                        </p>
                    </a>
                    <a
                        href="/reports"
                        className="p-4 rounded-lg border border-border-light dark:border-border-dark hover:bg-accent-light dark:hover:bg-accent-dark transition-colors"
                    >
                        <h4 className="font-semibold mb-1">Review Reports</h4>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            Handle content moderation
                        </p>
                    </a>
                    <a
                        href="/users"
                        className="p-4 rounded-lg border border-border-light dark:border-border-dark hover:bg-accent-light dark:hover:bg-accent-dark transition-colors"
                    >
                        <h4 className="font-semibold mb-1">Manage Users</h4>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            View and edit user accounts
                        </p>
                    </a>
                </div>
            </Card>
        </div>
    );
}

function formatActionLabel(action) {
    const labels = {
        user_banned: 'User Banned',
        user_unbanned: 'User Unbanned',
        verification_approved: 'Verification Approved',
        verification_rejected: 'Verification Rejected',
        community_approved: 'Community Approved',
        community_rejected: 'Community Rejected',
        report_resolved: 'Report Resolved',
        post_deleted: 'Post Deleted',
        comment_deleted: 'Comment Deleted',
        admin_created: 'Admin Created',
        admin_removed: 'Admin Removed',
        admin_privileges_updated: 'Admin Privileges Updated',
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

